import { motion } from 'framer-motion'
import { Minus, Plus, HelpCircle, Volume2, VolumeX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { MAX_PAYLINE, MIN_PAYLINE } from '../utils/slotsLogic'
import type { ChipValue } from '@/types'
import { useState } from 'react'

interface ControlsProps {
  balance: number
  betPerLine: ChipValue
  activePaylines: number
  totalBet: number
  isSpinning: boolean
  freeSpinsRemaining: number
  onBetChange: (bet: ChipValue) => void
  onPaylinesChange: (lines: number) => void
  onShowPayTable: () => void
}

const CHIP_VALUES: ChipValue[] = [50, 100, 500, 1000, 5000]

const chipColors: Record<ChipValue, { bg: string; border: string; text: string }> = {
  50: {
    bg: 'from-green-600 via-green-500 to-green-600',
    border: 'border-green-400',
    text: 'text-white',
  },
  100: {
    bg: 'from-gray-200 via-white to-gray-200',
    border: 'border-gray-300',
    text: 'text-gray-900',
  },
  500: {
    bg: 'from-red-700 via-red-500 to-red-700',
    border: 'border-red-400',
    text: 'text-white',
  },
  1000: {
    bg: 'from-blue-700 via-blue-500 to-blue-700',
    border: 'border-blue-400',
    text: 'text-white',
  },
  5000: {
    bg: 'from-yellow-600 via-casino-gold to-yellow-600',
    border: 'border-yellow-400',
    text: 'text-black',
  },
}

export function Controls({
  balance,
  betPerLine,
  activePaylines,
  totalBet,
  isSpinning,
  freeSpinsRemaining,
  onBetChange,
  onPaylinesChange,
  onShowPayTable,
}: ControlsProps) {
  const isFreeSpinMode = freeSpinsRemaining > 0
  const [isMuted, setIsMuted] = useState(false)

  return (
    <div className="relative">
      {/* Control Panel Background - Metallic Look */}
      <div
        className={cn(
          'relative w-full',
          'bg-gradient-to-b from-zinc-700 via-zinc-800 to-zinc-900',
          'border-t-4 border-zinc-500',
          'shadow-[inset_0_4px_6px_rgba(0,0,0,0.4)]'
        )}
      >
        {/* Top Metal Strip */}
        <div className="h-2 bg-gradient-to-b from-zinc-400 via-zinc-500 to-zinc-600" />

        {/* Main Controls Area */}
        <div className="p-4 md:p-6">
          {/* Info Display Panel - LCD Style */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-4 md:gap-8">
              {/* Balance Display */}
              <div className="relative">
                <div
                  className={cn(
                    'px-4 py-2 rounded-lg',
                    'bg-gradient-to-b from-gray-900 to-black',
                    'border-2 border-zinc-600',
                    'shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)]'
                  )}
                >
                  <div className="text-[10px] text-green-500/70 uppercase tracking-wider font-mono">
                    CREDITO
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-green-400 font-mono tracking-wider">
                    ${balance.toLocaleString()}
                  </div>
                </div>
                {/* LED indicator */}
                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-green-500 shadow-[0_0_6px_#22c55e]" />
              </div>

              {/* Total Bet Display */}
              <div className="relative">
                <div
                  className={cn(
                    'px-4 py-2 rounded-lg',
                    'bg-gradient-to-b from-gray-900 to-black',
                    'border-2 border-zinc-600',
                    'shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)]'
                  )}
                >
                  <div className="text-[10px] text-casino-gold/70 uppercase tracking-wider font-mono">
                    APUESTA
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-casino-gold font-mono tracking-wider">
                    ${totalBet.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            {/* Utility Buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMuted(!isMuted)}
                className="text-zinc-400 hover:text-white hover:bg-zinc-700"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowPayTable}
                className="text-zinc-400 hover:text-casino-gold hover:bg-zinc-700"
              >
                <HelpCircle className="w-5 h-5 mr-1" />
                <span className="hidden sm:inline">PAGOS</span>
              </Button>
            </div>
          </div>

          {/* Main Control Row */}
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Left: Paylines Control */}
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'flex items-center gap-2 p-2 rounded-xl',
                  'bg-gradient-to-b from-zinc-800 to-zinc-900',
                  'border border-zinc-600'
                )}
              >
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onPaylinesChange(Math.max(MIN_PAYLINE, activePaylines - 1))}
                  disabled={isSpinning || activePaylines <= MIN_PAYLINE}
                  className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    'bg-gradient-to-b from-zinc-600 to-zinc-700',
                    'border-2 border-zinc-500',
                    'text-white font-bold',
                    'shadow-[inset_0_-2px_4px_rgba(0,0,0,0.3)]',
                    'active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <Minus className="w-5 h-5" />
                </motion.button>

                <div className="text-center min-w-[70px]">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Líneas</div>
                  <div className="text-2xl font-bold text-white font-mono">{activePaylines}</div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onPaylinesChange(Math.min(MAX_PAYLINE, activePaylines + 1))}
                  disabled={isSpinning || activePaylines >= MAX_PAYLINE}
                  className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    'bg-gradient-to-b from-zinc-600 to-zinc-700',
                    'border-2 border-zinc-500',
                    'text-white font-bold',
                    'shadow-[inset_0_-2px_4px_rgba(0,0,0,0.3)]',
                    'active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <Plus className="w-5 h-5" />
                </motion.button>
              </div>
            </div>

            {/* Center: Bet Chips */}
            <div className="flex gap-2 md:gap-3">
              {CHIP_VALUES.map((value) => {
                const colors = chipColors[value]
                const isSelected = betPerLine === value

                return (
                  <motion.button
                    key={value}
                    whileHover={{ scale: 1.1, y: -4 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onBetChange(value)}
                    disabled={isSpinning}
                    className={cn(
                      'relative w-12 h-12 md:w-14 md:h-14 rounded-full',
                      'font-bold text-xs md:text-sm',
                      'bg-gradient-to-b shadow-lg',
                      colors.bg,
                      colors.text,
                      // Chip edge effect
                      'border-4',
                      colors.border,
                      // Inner dashed circle
                      'before:absolute before:inset-2 before:rounded-full',
                      'before:border-2 before:border-dashed before:border-white/30',
                      // Selection state
                      isSelected && [
                        'ring-4 ring-casino-gold ring-offset-2 ring-offset-zinc-800',
                        'shadow-[0_0_20px_rgba(212,175,55,0.6)]',
                        'scale-110',
                      ],
                      isSpinning && 'opacity-50 cursor-not-allowed',
                      'transition-all duration-200'
                    )}
                  >
                    <span className="relative z-10 drop-shadow-md">
                      {value >= 1000 ? `${value / 1000}K` : value}
                    </span>
                    {/* Chip shine */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/30 via-transparent to-transparent" />
                  </motion.button>
                )
              })}
            </div>

          </div>

          {/* Free Spins Banner */}
          {isFreeSpinMode && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 text-center"
            >
              <div
                className={cn(
                  'inline-flex items-center gap-2 px-6 py-2 rounded-full',
                  'bg-gradient-to-r from-green-600/80 via-green-500/80 to-green-600/80',
                  'border-2 border-green-400',
                  'shadow-[0_0_20px_rgba(34,197,94,0.4)]'
                )}
              >
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                  className="text-xl"
                >
                  🎁
                </motion.span>
                <span className="font-bold text-white">
                  ¡{freeSpinsRemaining} GIROS GRATIS!
                </span>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom Metal Strip with Screws */}
        <div className="h-3 bg-gradient-to-b from-zinc-600 via-zinc-700 to-zinc-800 flex items-center justify-between px-8">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-gradient-to-br from-zinc-400 to-zinc-600 shadow-inner"
            />
          ))}
        </div>
      </div>
    </div>
  )
}
